import {
  Autocomplete,
  ListItemProps,
  MenuItem,
  TextField,
  TextFieldProps,
} from '@mui/material';
import { Field, useFormikContext } from 'formik';
import { camelCase, get, isObject, isString } from 'lodash';
import React, { useCallback, useEffect, useMemo } from 'react';

import { InputComponent } from '..';
import { normalizeSize } from '../helpers';

export type InputSelectOption =
  | {
      value: string | number;
      label: string;
      disabled?: boolean;
      group?: string;
    }
  | string
  | number;

export interface InputSelectProps extends InputComponent {
  options?: InputSelectOption[];
  autoSelectFirstOption?: boolean;
  sx?: TextFieldProps['sx'];
}

export function InputSelect({
  label,
  options,
  disabled,
  hint,
  required,
  nameOverride,
  hidden,
  size: propSize,
  readOnly = false,
  autoSelectFirstOption = true,
  sx = {},
}: InputSelectProps) {
  const { values, setFieldValue, errors } =
    useFormikContext<Record<string, string>>();

  const validate = useCallback(
    (value: string | number | undefined) => {
      if (required && (!value || value === undefined)) {
        return `${label} is required.`;
      }
      return undefined;
    },
    [label, required],
  );

  const name = useMemo(
    () => nameOverride ?? camelCase(label),
    [label, nameOverride],
  );

  const size = normalizeSize(propSize);

  function renderOption(
    option: InputSelectOption,
    props: ListItemProps,
  ): React.ReactElement {
    return isObject(option) ? (
      <MenuItem value={option.value} disabled={option.disabled} {...props}>
        {option.label}
      </MenuItem>
    ) : (
      <MenuItem
        value={isString(option) ? camelCase(option) : option}
        {...props}
      >
        {option}
      </MenuItem>
    );
  }

  useEffect(() => {
    if (autoSelectFirstOption && options?.length === 1) {
      const opt = options[0];
      setFieldValue(name, typeof opt === 'object' ? opt.value : opt);
    }
  }, [autoSelectFirstOption, options, name, setFieldValue]);

  const selectedOption = useMemo(
    () =>
      options?.find((opt) => {
        if (isObject(opt)) {
          return opt.value === get(values, name);
        }
        return opt === get(values, name);
      }),
    [name, values, options],
  );

  return (
    <Field name={name} validate={validate}>
      {() => (
        <Autocomplete<InputSelectOption, false, boolean, false>
          disabled={disabled || readOnly}
          hidden={hidden}
          fullWidth
          options={options ?? []}
          disableClearable={required}
          value={selectedOption ?? null}
          sx={sx}
          renderOption={(props: ListItemProps, option: InputSelectOption) =>
            renderOption(option, props)
          }
          getOptionLabel={(option: InputSelectOption) =>
            isObject(option) ? option.label : String(option)
          }
          renderInput={(params: TextFieldProps) => (
            <TextField
              {...params}
              label={label}
              size={size as TextFieldProps['size']}
              required={required && !readOnly}
              error={!!errors[name]}
              helperText={errors[name] ? errors[name] : hint}
            />
          )}
          onChange={(
            _evt: React.SyntheticEvent,
            value: InputSelectOption | null,
          ) => {
            if (!readOnly) {
              setFieldValue(name, isObject(value) ? value.value : value);
            }
          }}
        />
      )}
    </Field>
  );
}
